import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import VideoGrid from "@/components/feature/VideoGrid";
import EmptyState from "@/components/base/EmptyState";
import SectionHeading from "@/components/feature/SectionHeading";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { regionOptions, yearOptions } from "@/mocks/videos";
import { filterVideos, getHotVideos } from "@/utils/videoFilters";

const typeOptions = [
  { key: "all", label: "全部类型" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
  { key: "variety", label: "综艺" },
];

function FilterSelect({
  value,
  options,
  onChange,
  label,
}: {
  value: string;
  options: { key: string; label: string }[];
  onChange: (v: string) => void;
  label: string;
}) {
  return (
    <label className="inline-flex items-center gap-2">
      <span className="text-sm text-foreground-500 whitespace-nowrap">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 px-3 pr-8 rounded-md bg-background-100 text-sm text-foreground-900 outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer appearance-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 0.5rem center",
          backgroundSize: "1rem",
        }}
      >
        {options.map((opt) => (
          <option key={opt.key} value={opt.key}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";

  const [inputValue, setInputValue] = useState(query);
  const [type, setType] = useState("all");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");

  useDocumentTitle(
    query ? `搜索“${query}” - 99视频` : "搜索 - 99视频",
    query
      ? `99视频搜索“${query}”的结果，涵盖电影、电视剧、综艺等高清影视资源，可按类型、地区、年份进一步筛选。`
      : "在99视频搜索海量高清影视资源，涵盖电影、电视剧、综艺等多种类型。"
  );

  useEffect(() => {
    setInputValue(query);
  }, [query]);

  const results = useMemo(
    () =>
      filterVideos({
        keyword: query,
        type,
        region,
        year,
      }),
    [query, type, region, year]
  );

  const hotRecommend = useMemo(() => getHotVideos(5), []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const kw = inputValue.trim();
    if (kw) {
      setSearchParams({ q: kw });
    } else {
      setSearchParams({});
    }
  };

  const handleReset = () => {
    setType("all");
    setRegion("全部");
    setYear("全部");
  };

  return (
    <div className="w-full">
      <div className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full max-w-page mx-auto px-4 md:px-6 py-8 md:py-10">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
            搜索影视内容
          </h1>
          <form
            onSubmit={handleSubmit}
            className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 max-w-3xl"
          >
            <div className="flex-1 flex items-center gap-2 h-12 px-4 rounded-md bg-background-50 border border-background-200 focus-within:ring-2 focus-within:ring-primary-400">
              <i className="ri-search-line text-xl text-foreground-500"></i>
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="输入影视名称、类型或演员关键词"
                className="flex-1 min-w-0 text-sm text-foreground-900 bg-transparent outline-none placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="h-12 px-8 rounded-md bg-primary-600 text-background-50 text-sm font-semibold hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="w-full max-w-page mx-auto px-4 md:px-6 py-8 md:py-10">
        {query && (
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <p className="text-sm text-foreground-600">
              关键词“<span className="font-semibold text-primary-700">{query}</span>”
              共找到 <span className="font-semibold text-accent-700">{results.length}</span> 个结果
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <FilterSelect value={type} options={typeOptions} onChange={setType} label="类型" />
              <FilterSelect
                value={region}
                options={regionOptions.map((r) => ({ key: r, label: r }))}
                onChange={setRegion}
                label="地区"
              />
              <FilterSelect
                value={year}
                options={yearOptions.map((y) => ({ key: y, label: y }))}
                onChange={setYear}
                label="年份"
              />
              <button
                type="button"
                onClick={handleReset}
                className="text-sm text-secondary-700 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                重置
              </button>
            </div>
          </div>
        )}

        {!query ? (
          <div className="py-8">
            <EmptyState
              icon="ri-search-line"
              title="输入关键词开始搜索"
              description="试试搜索“星际远征”“长安迷雾”或“悬疑”“科幻”等关键词。"
            />
            <div className="mt-10">
              <SectionHeading
                icon="ri-fire-line"
                title="热门推荐"
                subtitle="不知道看什么？看看大家都在追的内容"
              />
              <VideoGrid videos={hotRecommend} />
            </div>
          </div>
        ) : results.length === 0 ? (
          <div>
            <EmptyState
              icon="ri-emotion-sad-line"
              title="未找到相关影视内容"
              description={`没有找到与“${query}”相关的影视资源，换个关键词或看看下面的热门推荐吧。`}
            />
            <div className="mt-8">
              <SectionHeading
                icon="ri-fire-line"
                title="热门推荐"
                subtitle="换个方向，也许会有惊喜"
              />
              <VideoGrid videos={hotRecommend} />
            </div>
          </div>
        ) : (
          <VideoGrid videos={results} />
        )}

        {!query && (
          <div className="mt-8 text-center">
            <Link
              to="/category/movie"
              className="inline-flex items-center gap-1.5 text-sm text-primary-700 hover:text-primary-500 transition-colors cursor-pointer"
            >
              或直接浏览全部电影
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}